import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';

const MenuIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" />
  </svg>
);

export default function Header({ title = 'Overview', theme, onThemeChange, onOpenMenu, onDateRangeChange, activeApps, selectedAppId, onAppSelect, checking = false }) {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'days').toDate(),
    dayjs().toDate()
  ]);
  const [startDate, endDate] = dateRange;
  const [showCalendar, setShowCalendar] = useState(false);
  const [showAppFilter, setShowAppFilter] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [isCompact, setIsCompact] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const updateLayout = event => setIsCompact(event.matches);
    setIsCompact(media.matches);
    media.addEventListener('change', updateLayout);
    return () => media.removeEventListener('change', updateLayout);
  }, []);

  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${dayjs(startDate).format('MMM DD')} - ${dayjs(endDate).format('MMM DD, YYYY')}`;
    }
    return 'Select date range';
  };

  const handleCheck = () => {
    if (startDate && endDate && onDateRangeChange) {
      const fromFormatted = dayjs(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss');
      const toFormatted = dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss');
      onDateRangeChange(fromFormatted, toFormatted);
      setShowCalendar(false); // Close calendar after checking
    }
  };

  return (
    <div className="topbar">
      <div className="topbar-left">
        <div className="topbar-heading">
          <span className="topbar-title">{title}</span>
        </div>

        <button className="app-mobilebar-menu" onClick={onOpenMenu} aria-label="Open menu">
          <MenuIcon />
        </button>
      </div>

      <div className="topbar-actions">
        <div className="theme-picker">
          <button className="theme-toggle" onClick={() => setShowThemeMenu(!showThemeMenu)} aria-label="Change color theme">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41"/></svg>
          </button>
          {showThemeMenu && (
            <div className="theme-menu">
              {['light', 'dark', 'system'].map(option => (
                <button key={option} className={theme === option ? 'active' : ''} onClick={() => { onThemeChange(option); setShowThemeMenu(false); }}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="date-range-picker-inline">
          <button
            className="date-picker-btn"
            onClick={() => setShowCalendar(!showCalendar)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span className="date-picker-btn-text">{formatDateRange()}</span>
          </button>

          {showCalendar && (
            <div className="calendar-dropdown">
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  setDateRange(update);
                }}
                maxDate={dayjs().toDate()}
                monthsShown={isCompact ? 1 : 2}
                inline
                calendarClassName="custom-calendar"
              />
            </div>
          )}
        </div>

        <button className="btn-check" onClick={handleCheck} disabled={checking}>
          {checking ? (
            <span className="loader-ring loader-ring-sm" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          )}
          {checking ? 'Checking' : 'Check'}
        </button>

        <div className="filter-dropdown-container">
          <button
            className="btn-filters"
            onClick={() => setShowAppFilter(!showAppFilter)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            {selectedAppId ? `App #${selectedAppId}` : 'Filters'}
          </button>

          {showAppFilter && (
            <div className="filter-dropdown">
              <div className="filter-dropdown-header">
                <span>Filter by App</span>
                <button
                  className="btn-close-filter"
                  onClick={() => setShowAppFilter(false)}
                  aria-label="Close app filter"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="filter-dropdown-content">
                <button
                  className={`filter-option ${!selectedAppId ? 'active' : ''}`}
                  onClick={() => {
                    onAppSelect && onAppSelect(null);
                    setShowAppFilter(false);
                  }}
                >
                  <span>All Apps</span>
                  <span className="filter-badge">Show All</span>
                </button>
                {activeApps && activeApps.map(app => (
                  <button
                    key={app.app_id}
                    className={`filter-option ${selectedAppId === app.app_id ? 'active' : ''}`}
                    onClick={() => {
                      onAppSelect && onAppSelect(app.app_id);
                      setShowAppFilter(false);
                    }}
                  >
                    <span>#{app.app_id}</span>
                    <span className="filter-badge">${app.commission.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
